/**
 * FileMenu — New / Save / Open / Export / Import controls.
 *
 * Two modals (Save and Open). Export downloads a .pwt JSON file, Import
 * accepts one from disk. Auto-save status shows when the last write to
 * IndexedDB happened.
 */

import { useEffect, useRef, useState } from 'react';
import type { DemoSong } from '../audio/demo-song';
import {
  deleteSnapshot,
  listSnapshots,
  loadSnapshot,
  makeSnapshot,
  parseFromImport,
  saveSnapshot,
  serializeForExport,
  type SnapshotMeta,
} from '../lib/persistence';

interface Props {
  song: DemoSong;
  onLoadSong: (song: DemoSong) => void;
  /** Replace the in-memory song with a fresh empty one. */
  onNewSong: () => void;
  /** Used in the auto-save status row. */
  lastAutosavedAt: string | null;
  autosavePending: boolean;
}

type Modal = null | { kind: 'save' } | { kind: 'open'; snapshots: SnapshotMeta[] };

export function FileMenu({
  song,
  onLoadSong,
  onNewSong,
  lastAutosavedAt,
  autosavePending,
}: Props) {
  const [modal, setModal] = useState<Modal>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveName, setSaveName] = useState(song.title);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setSaveName(song.title);
  }, [song.title]);

  async function openSaveModal() {
    setError(null);
    setModal({ kind: 'save' });
  }

  async function openOpenModal() {
    try {
      const snapshots = await listSnapshots();
      setError(null);
      setModal({ kind: 'open', snapshots });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleSave() {
    try {
      if (!saveName.trim()) {
        setError('Snapshot name is required.');
        return;
      }
      await saveSnapshot(saveName, song);
      setModal(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleLoad(name: string) {
    try {
      const snap = await loadSnapshot(name);
      if (!snap) throw new Error('Snapshot not found.');
      onLoadSong(snap.song);
      setModal(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleDelete(name: string) {
    try {
      await deleteSnapshot(name);
      const snapshots = await listSnapshots();
      setModal({ kind: 'open', snapshots });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function handleExport() {
    try {
      const snap = makeSnapshot(song.title || 'Untitled', song);
      const text = serializeForExport(snap);
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(song.title || 'untitled').replace(/[^a-zA-Z0-9-_]+/g, '_')}.pwt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function handleImportClick() {
    fileInputRef.current?.click();
  }

  async function handleImportFile(file: File) {
    try {
      const text = await file.text();
      const snap = parseFromImport(text);
      onLoadSong(snap.song);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function handleNewSong() {
    if (
      !window.confirm(
        'Start a new song? Unsaved changes are preserved in the auto-save and can be recovered.',
      )
    )
      return;
    onNewSong();
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-nes-white/80">
      <button
        onClick={handleNewSong}
        className="rounded border border-nes-white/30 px-2 py-1 hover:bg-nes-white/5"
        title="Start a new empty song"
      >
        New
      </button>
      <button
        onClick={openSaveModal}
        className="rounded border border-nes-cyan/40 px-2 py-1 text-nes-cyan hover:bg-nes-cyan/10"
        title="Save current song as a named snapshot"
      >
        Save
      </button>
      <button
        onClick={openOpenModal}
        className="rounded border border-nes-cyan/40 px-2 py-1 text-nes-cyan hover:bg-nes-cyan/10"
        title="Load a saved snapshot"
      >
        Open
      </button>
      <button
        onClick={handleExport}
        className="rounded border border-nes-white/30 px-2 py-1 hover:bg-nes-white/5"
        title="Download song as .pwt JSON"
      >
        ↓ .pwt
      </button>
      <button
        onClick={handleImportClick}
        className="rounded border border-nes-white/30 px-2 py-1 hover:bg-nes-white/5"
        title="Import a .pwt JSON file"
      >
        ↑ .pwt
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pwt,application/json"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleImportFile(f);
          e.target.value = '';
        }}
      />
      <span className="ml-2 text-[10px] text-nes-white/40">
        {autosavePending
          ? 'autosaving…'
          : lastAutosavedAt
            ? `autosaved ${new Date(lastAutosavedAt).toLocaleTimeString()}`
            : 'no autosave yet'}
      </span>

      {error && (
        <span className="ml-2 text-nes-red" role="alert">
          {error}
        </span>
      )}

      {modal?.kind === 'save' && (
        <Modal title="Save snapshot" onClose={() => setModal(null)}>
          <label className="flex flex-col gap-1 text-xs">
            <span className="text-nes-white/70">Name</span>
            <input
              autoFocus
              type="text"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSave();
              }}
              className="rounded border border-nes-cyan/30 bg-nes-black px-2 py-1 text-nes-white"
            />
          </label>
          <div className="mt-3 flex justify-end gap-2">
            <button
              onClick={() => setModal(null)}
              className="rounded border border-nes-white/30 px-3 py-1 text-xs hover:bg-nes-white/5"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="rounded bg-nes-cyan px-3 py-1 text-xs font-bold text-nes-black hover:bg-nes-cyan/80"
            >
              Save
            </button>
          </div>
        </Modal>
      )}

      {modal?.kind === 'open' && (
        <Modal title="Open snapshot" onClose={() => setModal(null)}>
          {modal.snapshots.length === 0 ? (
            <p className="text-xs text-nes-white/50">No snapshots saved yet.</p>
          ) : (
            <ul className="flex max-h-72 flex-col gap-1 overflow-y-auto text-xs">
              {modal.snapshots.map((s) => (
                <li
                  key={s.name}
                  className="flex items-center justify-between gap-3 rounded border border-nes-cyan/20 bg-nes-black px-2 py-1"
                >
                  <div className="flex flex-col">
                    <span className="font-bold text-nes-cyan">{s.name}</span>
                    <span className="text-[10px] text-nes-white/40">
                      {new Date(s.savedAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => void handleLoad(s.name)}
                      className="rounded bg-nes-cyan px-2 py-[2px] text-[10px] font-bold text-nes-black hover:bg-nes-cyan/80"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete snapshot "${s.name}"?`))
                          void handleDelete(s.name);
                      }}
                      className="rounded border border-nes-red/40 px-2 py-[2px] text-[10px] text-nes-red hover:bg-nes-red/10"
                    >
                      ×
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => setModal(null)}
              className="rounded border border-nes-white/30 px-3 py-1 text-xs hover:bg-nes-white/5"
            >
              Close
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded border border-nes-cyan/40 bg-nes-dim p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-3 text-sm font-bold text-nes-cyan">{title}</h3>
        {children}
      </div>
    </div>
  );
}
